import { type MouseEventHandler, useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';

import { type SelectionMouseModifiers } from '~/hooks/useSelectableCard';

export type UseSingleDoubleClickHandlerParams = {
  /**
   * Callback to call before selection (pre-highlight the item). Receives the modifier
   * keys captured from the originating mousedown.
   */
  onSingleClickStart: (modifiers: SelectionMouseModifiers) => void;
  /**
   * Callback to confirm the selection. Receives the modifier keys captured from the
   * originating mousedown (so a delayed/deferred confirm still knows shift/cmd state).
   */
  onSingleClickComplete: (modifiers: SelectionMouseModifiers) => void;
  /**
   * Whether the delayed behavior should be used.
   * If false, uses immediate click behavior (original behavior).
   * If true, delays the click to distinguish from double-click.
   */
  shouldDelayClick: boolean;
  /**
   * Whether the element is enabled for click handling
   */
  isEnabled: boolean;
  /**
   * Delay in milliseconds to wait before confirming single-click
   */
  delayMs: number;
};

export type UseSingleDoubleClickHandlerReturn = {
  onMouseDown: MouseEventHandler<HTMLButtonElement>;
};

/**
 * Hook to handle single vs double-click detection with configurable behavior.
 *
 * Returns an onMouseDown handler that should be attached to your button/element.
 * The mouseup event is handled internally via window event listeners.
 *
 * When shouldDelayClick is false:
 * - Uses immediate click behavior for better responsiveness
 * - onSingleClickStart called on mousedown
 * - onSingleClickComplete called on mouseup
 *
 * When shouldDelayClick is true:
 * - Delays click handling to distinguish single from double-click
 * - Single click: waits {delayMs}ms before calling onSingleClickStart, then onSingleClickComplete
 * - Double click: cancels the timeout, neither callback is called
 *
 * This is useful for gallery items where you want instant feedback when starting fresh,
 * but smart double-click handling when items are already selected.
 */
export const useSingleDoubleClickHandler = ({
  onSingleClickStart,
  onSingleClickComplete,
  shouldDelayClick,
  isEnabled,
  delayMs,
}: UseSingleDoubleClickHandlerParams): UseSingleDoubleClickHandlerReturn => {
  const isMouseDownRef = useRef<boolean>();
  const singleClickTimeoutRef = useRef<NodeJS.Timeout | null>();
  const mouseUpOccurredRef = useRef<boolean>();
  const cancelDragListenerRef = useRef<(() => void) | null>();
  // Modifier keys captured from the mousedown that began this interaction, so the
  // deferred (timeout) and window-mouseup callbacks can report shift/cmd without an event.
  const modifiersRef = useRef<SelectionMouseModifiers>({ shiftKey: false, metaKey: false, ctrlKey: false });

  // Store callbacks in refs to avoid stale closures in setTimeout
  const onSingleClickStartRef = useRef(onSingleClickStart);
  const onSingleClickCompleteRef = useRef(onSingleClickComplete);

  useEffect(() => {
    onSingleClickStartRef.current = onSingleClickStart;
    onSingleClickCompleteRef.current = onSingleClickComplete;
  }, [onSingleClickStart, onSingleClickComplete]);

  // Callback refreshes must not cancel the pending click through the listener cleanup.
  const onMouseUp = useCallback(() => {
    isMouseDownRef.current = false;
    mouseUpOccurredRef.current = true;

    if (!singleClickTimeoutRef.current) {
      flushSync(() => {
        if (isEnabled) {
          onSingleClickCompleteRef.current(modifiersRef.current);
        }
      });
    }
  }, [isEnabled]);

  useEffect(() => {
    if (isMouseDownRef.current) {
      window.addEventListener('mouseup', onMouseUp, { once: true });
    }

    return () => {
      window.removeEventListener('mouseup', onMouseUp);
      if (singleClickTimeoutRef.current) {
        clearTimeout(singleClickTimeoutRef.current);
        singleClickTimeoutRef.current = null;
      }
      mouseUpOccurredRef.current = false;
    };
  }, [onMouseUp]);

  // Separate unmount-only cleanup for the dragstart guard — keeps it alive across
  // onMouseUp dep changes so a pending drag interaction isn't left without its guard.
  useEffect(() => {
    return () => {
      if (cancelDragListenerRef.current) {
        window.removeEventListener('dragstart', cancelDragListenerRef.current);
        cancelDragListenerRef.current = null;
      }
    };
  }, []);

  const onMouseDown = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      flushSync(() => {
        if (event.button === 0) {
          if (!isEnabled) {
            return;
          }

          // Capture modifier state at press time; reused by the deferred + mouseup callbacks.
          modifiersRef.current = { shiftKey: event.shiftKey, metaKey: event.metaKey, ctrlKey: event.ctrlKey };

          if (shouldDelayClick) {
            if (event.detail === 2) {
              if (singleClickTimeoutRef.current) {
                clearTimeout(singleClickTimeoutRef.current);
                singleClickTimeoutRef.current = null;
              }
              if (cancelDragListenerRef.current) {
                window.removeEventListener('dragstart', cancelDragListenerRef.current);
                cancelDragListenerRef.current = null;
              }

              return;
            }

            isMouseDownRef.current = true;
            mouseUpOccurredRef.current = false;
            window.addEventListener('mouseup', onMouseUp, { once: true });

            // If a drag starts before the timeout fires, cancel selection entirely.
            // Without this, the 300ms delay fires after drag end and selects the item.
            const onDragStart = () => {
              if (singleClickTimeoutRef.current) {
                clearTimeout(singleClickTimeoutRef.current);
                singleClickTimeoutRef.current = null;
              }
              window.removeEventListener('mouseup', onMouseUp);
              isMouseDownRef.current = false;
              mouseUpOccurredRef.current = false;
              cancelDragListenerRef.current = null;
            };
            cancelDragListenerRef.current = onDragStart;
            window.addEventListener('dragstart', onDragStart, { once: true });

            singleClickTimeoutRef.current = setTimeout(() => {
              // Confirmed click (not a drag) — clean up the dragstart guard
              if (cancelDragListenerRef.current) {
                window.removeEventListener('dragstart', cancelDragListenerRef.current);
                cancelDragListenerRef.current = null;
              }

              onSingleClickStartRef.current(modifiersRef.current);
              singleClickTimeoutRef.current = null;

              if (mouseUpOccurredRef.current) {
                // Defer to next tick to allow React state updates from onSingleClickStart
                setTimeout(() => {
                  if (isEnabled) {
                    onSingleClickCompleteRef.current(modifiersRef.current);
                  }
                }, 0);
              }
            }, delayMs);
          } else {
            isMouseDownRef.current = true;
            onSingleClickStart(modifiersRef.current);
            window.addEventListener('mouseup', onMouseUp, { once: true });
          }
        }
      });
    },
    [isEnabled, onMouseUp, shouldDelayClick, delayMs, onSingleClickStart],
  );

  return {
    onMouseDown,
  };
};
